import { FormHelperText, Switch, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BranchService } from "../../../../../services/branch-service";
import { BranchBasicInfo } from "../../../../../services/dto/request/branches-request-dto";
import { PaycloudCatalogItem } from "../../../../../services/dto/response/branches-response-dt";

const textFieldStyles = {
  "& .MuiInput-root": {
    color: "#000",
    fontFamily: "Montserrat",
    "&:before": {
      borderBottom: "1px solid #838383",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: "#898989",
    },
  },
  "& label.Mui-focused": {
    color: "#7e7e7e",
  },
  "& .MuiInputLabel-standard": {
    color: "#000",
    fontWeight: "400",
    fontFamily: "Montserrat",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#838383",
  },
  "& .Mui-error": {
    color: "#d32f2f",
  },
  "& .MuiFormLabel-root.Mui-error": {
    color: "#d32f2f",
  },
  "& .MuiInput-underline.Mui-error:before": {
    borderBottomColor: "#d32f2f",
  },
  "& .MuiInput-underline.Mui-error:after": {
    borderBottomColor: "#d32f2f",
  },
};

const switchStyles = {
  "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
    bgcolor: "#EF0BB8",
  },
  "& .MuiSwitch-track": {
    bgcolor: "#898989",
  },
  "& .MuiSwitch-thumb": {
    bgcolor: "#EF0BB8",
  },
};

interface KycFieldsARProps {
  branchBasicInfo: BranchBasicInfo;
  errors: Record<string, string>;
  disabled: boolean;
  handleBranchBasicInfoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSwitchChange: (e: React.ChangeEvent<HTMLInputElement>, type: "branchConfig" | "branchBasicInfo" | "manager") => void;
}

export function KycFieldsAR({
  branchBasicInfo,
  errors,
  disabled,
  handleBranchBasicInfoChange,
  handleSwitchChange,
}: KycFieldsARProps) {
  const { t } = useTranslation();
  const branchService = useMemo(() => new BranchService(), []);
  const [ivaCategories, setIvaCategories] = useState<PaycloudCatalogItem[]>([]);
  const [genders, setGenders] = useState<PaycloudCatalogItem[]>([]);
  const [civilStatuses, setCivilStatuses] = useState<PaycloudCatalogItem[]>([]);
  const [catalogError, setCatalogError] = useState("");

  useEffect(() => {
    branchService.getKycCatalogs(
      (data) => {
        setIvaCategories(data.ivaCategories ?? []);
        setGenders(data.genders ?? []);
        setCivilStatuses(data.civilStatuses ?? []);
      },
      (errorMessage) => {
        setCatalogError(errorMessage || t("branches.create.kyc-catalog-error"));
      }
    );
  }, [branchService, t]);

  const today = (() => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${month}-${day}`;
  })();

  return (
    <div className="flex flex-col gap-4 border-t pt-4">
      <div className="flex flex-col">
        <span className="text-black font-medium">{t("branches.create.kyc-title")}</span>
      </div>

      {catalogError && (
        <span className="text-sm text-red-500">{catalogError}</span>
      )}

      <TextField
        type="date"
        name="birthDate"
        value={branchBasicInfo.birthDate}
        onChange={handleBranchBasicInfoChange}
        error={!!errors.birthDate}
        helperText={errors.birthDate}
        label={t("branches.create.birth-date")}
        slotProps={{ htmlInput: { max: today }, inputLabel: { shrink: true } }}
        variant="standard"
        disabled={disabled}
        required
        fullWidth
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="occupation"
        value={branchBasicInfo.occupation}
        onChange={handleBranchBasicInfoChange}
        error={!!errors.occupation}
        helperText={errors.occupation || t("branches.create.occupation-note")}
        label={t("branches.create.occupation")}
        variant="standard"
        disabled={disabled}
        required
        fullWidth
        sx={textFieldStyles}
        slotProps={{ htmlInput: { maxLength: 40 } }}
      />

      <CatalogSelect
        id="ivaCondition"
        label={t("branches.create.iva-condition")}
        placeholder={t("branches.create.select-option")}
        value={branchBasicInfo.ivaCondition}
        options={ivaCategories}
        error={errors.ivaCondition}
        disabled={disabled}
        onChange={handleBranchBasicInfoChange}
      />

      <CatalogSelect
        id="gender"
        label={t("branches.create.gender")}
        placeholder={t("branches.create.select-option")}
        value={branchBasicInfo.gender}
        options={genders}
        error={errors.gender}
        disabled={disabled}
        onChange={handleBranchBasicInfoChange}
      />

      <CatalogSelect
        id="civilStatus"
        label={t("branches.create.civil-status")}
        placeholder={t("branches.create.select-option")}
        value={branchBasicInfo.civilStatus}
        options={civilStatuses}
        error={errors.civilStatus}
        disabled={disabled}
        onChange={handleBranchBasicInfoChange}
      />

      <div className="w-full flex justify-between items-center">
        <div className="flex flex-col">
          <label htmlFor="isPEP" className="text-black">
            {t("branches.create.is-pep")}
          </label>
          <FormHelperText sx={{ fontFamily: "Montserrat" }}>
            {t("branches.create.is-pep-note")}
          </FormHelperText>
        </div>
        <Switch
          id="isPEP"
          name="isPEP"
          checked={branchBasicInfo.isPEP}
          onChange={(e) => {
            handleSwitchChange(e, "branchBasicInfo");
            if (!e.target.checked) {
              handleBranchBasicInfoChange({
                target: { name: "pepPosition", value: "" },
              } as unknown as React.ChangeEvent<HTMLInputElement>);
            }
          }}
          disabled={disabled}
          sx={switchStyles}
        />
      </div>

      {branchBasicInfo.isPEP && (
        <TextField
          type="text"
          name="pepPosition"
          value={branchBasicInfo.pepPosition}
          onChange={handleBranchBasicInfoChange}
          error={!!errors.pepPosition}
          helperText={errors.pepPosition || t("branches.create.pep-position-note")}
          label={t("branches.create.pep-position")}
          variant="standard"
          disabled={disabled}
          required
          fullWidth
          sx={textFieldStyles}
          slotProps={{ htmlInput: { maxLength: 40 } }}
        />
      )}

      <div className="w-full flex justify-between items-center">
        <div className="flex flex-col">
          <label htmlFor="isUIF" className="text-black">
            {t("branches.create.is-uif")}
          </label>
        </div>
        <Switch
          id="isUIF"
          name="isUIF"
          checked={branchBasicInfo.isUIF}
          onChange={(e) => handleSwitchChange(e, "branchBasicInfo")}
          disabled={disabled}
          sx={switchStyles}
        />
      </div>
    </div>
  );
}

function CatalogSelect({
  id,
  label,
  placeholder,
  value,
  options,
  error,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  options: PaycloudCatalogItem[];
  error?: string;
  disabled: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-md text-black">
        {label}
      </label>
      <select
        id={id}
        name={id}
        required
        className={`w-full px-3 py-2 border rounded-md bg-white ${
          error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-primary"
        } focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed`}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e as unknown as React.ChangeEvent<HTMLInputElement>)}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.code} value={option.code}>
            {option.description}
          </option>
        ))}
      </select>
      {error && <span className="text-sm text-red-500">{error}</span>}
    </div>
  );
}
